import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TypographyH1 } from '@/components/ui/typography';
import { useMarketResearchByIdQuery } from '@/queries/useMarketResearchByIdQuery';
import { usePDVInMarketResearchQuery } from '@/queries/usepdvInMarketResearchQuery';
import { useNonResearchReasonQuery } from '@/queries/useNonResearchReasonQuery';
import {
  usePackagingsQuery,
  useProductsByPackagingQuery,
  useSavePackagingResearchMutation,
  usePublishClientResearchMutation,
} from '@/queries/usePackagingResearchQuery';
import { useToast } from '@/components/ui/use-toast';
import {
  CheckCircle2,
  Clock,
  Package,
  Store,
  ChevronLeft,
  Send,
  Check,
  Ban,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import Select from '@/components/Select';
import LoadingScreen from '@/pages/LoadingScreen';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { PackagingProduct } from '@/types/MarketResearch';
import SellerClientList from '../components/SellerClientList';
import { PDVMarketResearch } from '@/api/business/marketResearch/type';
import { NonResearchReason } from '@/types/NonResearchReason';
import { useQueryClient } from '@tanstack/react-query';

interface ProductFormState {
  VL_PRECO_COMPRA: number | string;
  QTD_VOLUME_COMPRA: number | string;
  VL_PRECO_VENDA: number | string;
  SK_MOTIVO_NAO_PESQUISA?: number;
  isNotResearched: boolean;
}

export default function PackagingResearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skPesquisa = Number(searchParams.get('search'));
  const paramClient = Number(searchParams.get('client')) || 0;

  const { toast } = useToast();
  const online = useOnlineStatus();

  // Selected state
  const [selectedCliente, setSelectedCliente] = useState<number>(paramClient);
  const [selectedPackaging, setSelectedPackaging] = useState<string | null>(
    null,
  );
  const [currentCoords, setCurrentCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const handleSelectSellerClient = (
    client: PDVMarketResearch,
    coords: { latitude: number; longitude: number },
  ) => {
    setSelectedCliente(client.SK_CLIENTE);
    setCurrentCoords(coords);
    setSelectedPackaging(null);
  };

  // Queries
  const { data: dataResearch, isPending: isPendingResearch } =
    useMarketResearchByIdQuery(skPesquisa);
  const marketResearch = dataResearch?.[0];

  const { data: dataPDV, isLoading: isLoadingPDV } =
    usePDVInMarketResearchQuery(skPesquisa);
  const { data: dataReasons } = useNonResearchReasonQuery(true);

  const {
    data: packagings,
    isLoading: isLoadingPackagings,
    refetch: refetchPackagings,
  } = usePackagingsQuery(
    skPesquisa,
    selectedCliente,
    Boolean(skPesquisa && selectedCliente),
  );

  const { data: packagingProducts, isLoading: isLoadingProducts } =
    useProductsByPackagingQuery(
      skPesquisa,
      selectedCliente,
      selectedPackaging || '',
      Boolean(skPesquisa && selectedCliente && selectedPackaging),
    );

  // Form state for products in packaging
  const [formProducts, setFormProducts] = useState<
    Record<string, ProductFormState>
  >({});

  const saveMutation = useSavePackagingResearchMutation();
  const publishMutation = usePublishClientResearchMutation();

  useEffect(() => {
    if (paramClient) {
      setSelectedCliente(paramClient);
    }
  }, [paramClient]);

  // Populate product form state when packagingProducts change
  useEffect(() => {
    if (packagingProducts && packagingProducts.length > 0) {
      const initialForm: Record<string, ProductFormState> = {};
      packagingProducts.forEach((p: PackagingProduct) => {
        const hasReason = Boolean(p.SK_MOTIVO_NAO_PESQUISA);
        initialForm[p.SK_PRODUTO] = {
          VL_PRECO_COMPRA: p.VL_PRECO_COMPRA ?? '',
          QTD_VOLUME_COMPRA: p.QTD_VOLUME_COMPRA ?? '',
          VL_PRECO_VENDA: p.VL_PRECO_VENDA ?? '',
          SK_MOTIVO_NAO_PESQUISA: p.SK_MOTIVO_NAO_PESQUISA || undefined,
          isNotResearched: hasReason,
        };
      });
      setFormProducts(initialForm);
    }
  }, [packagingProducts]);

  if (!skPesquisa) {
    navigate('/market-research');
    return null;
  }

  if (isPendingResearch) return <LoadingScreen />;

  // Currency helpers
  const formatCurrencyBR = (val: number | string) => {
    if (val === undefined || val === null || val === '') return '';
    const num =
      typeof val === 'string' ? Number(val.replace(/\D/g, '')) / 100 : val;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const parseCurrencyBR = (val: string) => {
    const num = Number(val.replace(/\D/g, '')) / 100;
    return isNaN(num) ? 0 : num;
  };

  // Field change
  const handleProductChange = (
    skProduto: string,
    field: keyof ProductFormState,
    value: any,
  ) => {
    setFormProducts((prev) => ({
      ...prev,
      [skProduto]: {
        ...prev[skProduto],
        [field]: value,
      },
    }));
  };

  // Toggle "Não Pesquisado"
  const handleToggleNotResearched = (skProduto: string, checked: boolean) => {
    setFormProducts((prev) => ({
      ...prev,
      [skProduto]: {
        ...prev[skProduto],
        isNotResearched: checked,
        SK_MOTIVO_NAO_PESQUISA: checked
          ? (dataReasons as NonResearchReason[] | undefined)?.[0]
              ?.SK_MOTIVO_NAO_PESQUISA || undefined
          : undefined,
        VL_PRECO_COMPRA: checked ? '' : prev[skProduto]?.VL_PRECO_COMPRA,
        QTD_VOLUME_COMPRA: checked ? '' : prev[skProduto]?.QTD_VOLUME_COMPRA,
        VL_PRECO_VENDA: checked ? '' : prev[skProduto]?.VL_PRECO_VENDA,
      },
    }));
  };

  // Validation before saving packaging (RF005, RN002)
  const validatePackagingForm = (): boolean => {
    if (!packagingProducts || packagingProducts.length === 0) return false;

    for (const prod of packagingProducts) {
      const state = formProducts[prod.SK_PRODUTO];
      if (!state) return false;

      if (state.isNotResearched) {
        if (!state.SK_MOTIVO_NAO_PESQUISA) {
          toast({
            title: `Selecione o motivo da não pesquisa para: ${prod.DS_PRODUTO}`,
            variant: 'destructive',
          });
          return false;
        }
      } else {
        const precoCompra = Number(state.VL_PRECO_COMPRA);
        const volume = Number(state.QTD_VOLUME_COMPRA);
        const precoVenda = Number(state.VL_PRECO_VENDA);

        if (isNaN(precoCompra) || precoCompra <= 0) {
          toast({
            title: `Preço de compra obrigatório para: ${prod.DS_PRODUTO}`,
            variant: 'destructive',
          });
          return false;
        }
        if (isNaN(volume) || volume < 0) {
          toast({
            title: `Volume semanal obrigatório para: ${prod.DS_PRODUTO}`,
            variant: 'destructive',
          });
          return false;
        }
        if (isNaN(precoVenda) || precoVenda <= 0) {
          toast({
            title: `Preço de venda obrigatório para: ${prod.DS_PRODUTO}`,
            variant: 'destructive',
          });
          return false;
        }
      }
    }

    return true;
  };
  const queryClient = useQueryClient();
  // Save packaging research (RF005, RF006, RN002)
  const handleSavePackaging = async () => {
    if (saveMutation.isPending || publishMutation.isPending) return;
    if (!validatePackagingForm()) return;
    if (!selectedPackaging || !selectedCliente) return;

    try {
      const payloadProducts = packagingProducts!.map((prod) => {
        const state = formProducts[prod.SK_PRODUTO];
        return {
          SK_PRODUTO: prod.SK_PRODUTO,
          VL_PRECO_COMPRA: state.isNotResearched
            ? undefined
            : Number(state.VL_PRECO_COMPRA),
          QTD_VOLUME_COMPRA: state.isNotResearched
            ? undefined
            : Number(state.QTD_VOLUME_COMPRA),
          VL_PRECO_VENDA: state.isNotResearched
            ? undefined
            : Number(state.VL_PRECO_VENDA),
          SK_MOTIVO_NAO_PESQUISA: state.isNotResearched
            ? state.SK_MOTIVO_NAO_PESQUISA
            : undefined,
          DD_LATITUDE: currentCoords?.latitude
            ? String(currentCoords.latitude)
            : undefined,
          DD_LONGITUDE: currentCoords?.longitude
            ? String(currentCoords.longitude)
            : undefined,
        };
      });

      await saveMutation.mutateAsync({
        SK_PESQUISA: skPesquisa,
        SK_CLIENTE: selectedCliente,
        DD_SKU: selectedPackaging,
        PRODUTOS: payloadProducts,
      });
      toast({
        title: `Embalagem ${selectedPackaging} concluída com sucesso!`,
      });

      await refetchPackagings();
      setSelectedPackaging(null); // Return to packaging list
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar embalagem',
        description: err?.response?.data?.message || 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  // Publish Client Research (RF007, RN005)
  const handlePublishClient = async () => {
    if (!selectedCliente) return;
    try {
      await publishMutation.mutateAsync({
        skPesquisa,
        skCliente: selectedCliente,
      });
      toast({
        title: 'Pesquisa do cliente publicada com sucesso!',
      });
      navigate(`/market-research/packaging-research?search=${skPesquisa}`);
      queryClient.refetchQueries({ queryKey: ['pdvinmarketresearch'] });
    } catch (err: any) {
      toast({
        title: 'Não foi possível publicar a pesquisa',
        description:
          err?.response?.data?.message ||
          'Verifique se todas as embalagens do cliente foram concluídas.',
        variant: 'destructive',
      });
    }
  };

  const selectedClientData = dataPDV?.find(
    (item) => item.SK_CLIENTE === selectedCliente,
  );
  const completedCount =
    packagings?.filter((p) => p.DD_STATUS === 'Concluída').length || 0;
  const totalCount = packagings?.length || 0;
  const allPackagingsComplete = totalCount > 0 && completedCount >= totalCount;

  return (
    <div className="flex flex-col h-full p-3 overflow-auto bg-gray-50 sm:p-4">
      {/* Top Title & Actions Bar */}
      <div className="flex items-center justify-between p-3 mb-4 bg-white border border-gray-200 shadow-sm sm:p-4 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10 text-amber-600">
            <Package size={22} />
          </div>
          <div>
            <TypographyH1 className="text-xl font-bold sm:text-2xl">
              {marketResearch?.NM_PESQUISA || `Pesquisa #${skPesquisa}`}
            </TypographyH1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
              online ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {online ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 w-full max-w-4xl mx-auto space-y-4">
        {/* STEP 1: Select Client with Location Validation & Visual Status (RF001, RF002, RF004, RF005, RF006) */}
        {!selectedCliente && (
          <SellerClientList
            clients={dataPDV || []}
            isLoading={isLoadingPDV}
            onSelectClient={handleSelectSellerClient}
          />
        )}

        {/* STEP 2: Packaging Selection Grid / List (RF002, RN003, RN006) */}
        {selectedCliente && !selectedPackaging ? (
          <div className="space-y-4">
            {/* Selected Client Card */}
            <div className="flex flex-col items-start justify-between gap-3 p-4 bg-white border border-gray-200 shadow-sm rounded-2xl sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Store size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold leading-tight text-gray-900">
                    {selectedClientData?.DS_NOME_FANTASIA ||
                      `Cliente #${selectedCliente}`}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Código: {selectedClientData?.NK_CLIENTE || '-'} • Cidade:{' '}
                    {selectedClientData?.DS_CIDADE || '-'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end w-full gap-2 sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setSelectedCliente(0)}
                >
                  Voltar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 p-2 text-xs"
                  onClick={() => refetchPackagings()}
                >
                  <RefreshCw size={15} />
                </Button>
              </div>
            </div>

            {/* Packaging Progress Header */}
            <div className="flex items-center justify-between px-1">
              <div>
                <h3 className="text-sm font-bold tracking-wide text-gray-800 uppercase">
                  Embalagens do Estabelecimento
                </h3>
                <p className="text-xs text-gray-500">
                  Selecione cada embalagem para preencher os produtos.
                </p>
              </div>
              <span className="px-3 py-1 text-xs font-semibold text-gray-700 bg-gray-200 rounded-full">
                {completedCount} / {totalCount} Concluídas
              </span>
            </div>

            {/* Packaging Cards Grid */}
            {isLoadingPackagings ? (
              <div className="py-12 text-xs text-center text-gray-400">
                Carregando embalagens disponíveis...
              </div>
            ) : !packagings || packagings.length === 0 ? (
              <div className="py-12 text-xs text-center text-gray-400 bg-white border border-gray-200 rounded-xl">
                Nenhuma embalagem cadastrada para esta pesquisa.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                {packagings.map((pkg) => {
                  const isComplete = pkg.DD_STATUS === 'Concluída';
                  return (
                    <div
                      key={pkg.DD_SKU}
                      onClick={() => setSelectedPackaging(pkg.DD_SKU)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between space-y-3 bg-white shadow-sm hover:shadow-md hover:border-primary ${
                        isComplete
                          ? 'border-green-300 bg-green-50/20'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            isComplete
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          <Package size={22} />
                        </div>

                        {isComplete ? (
                          <span className="flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                            <CheckCircle2 size={13} />
                            Concluída
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                            <Clock size={13} />
                            Pendente
                          </span>
                        )}
                      </div>

                      <div>
                        <h4 className="text-base font-bold text-gray-900">
                          {pkg.DD_SKU}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {pkg.TOTAL_PREENCHIDOS} de {pkg.TOTAL_PRODUTOS}{' '}
                          produtos respondidos
                        </p>
                      </div>

                      <div className="pt-1">
                        <Button
                          variant={isComplete ? 'outline' : 'default'}
                          size="sm"
                          className="w-full h-8 text-xs font-semibold"
                        >
                          {isComplete
                            ? 'Editar Preenchimento'
                            : 'Preencher Embalagem'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom Publication Action Bar (RF007, RN005) */}
            <div className="flex flex-col items-center justify-between gap-3 p-4 mt-6 bg-white border border-gray-200 shadow-sm rounded-2xl sm:flex-row">
              <div className="text-center sm:text-left">
                <h4 className="text-xs font-bold text-gray-800">
                  Publicação da Pesquisa do Cliente
                </h4>
                <p className="text-xs text-gray-500">
                  {allPackagingsComplete
                    ? 'Todas as embalagens foram concluídas! A pesquisa pode ser publicada.'
                    : 'Conclua o preenchimento de todas as embalagens para habilitar a publicação.'}
                </p>
              </div>

              <Button
                onClick={handlePublishClient}
                disabled={!allPackagingsComplete}
                className={`w-full sm:w-auto px-6 h-11 text-xs font-bold gap-2 ${
                  allPackagingsComplete
                    ? 'bg-primary text-white hover:bg-primary/90 shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send size={16} />
                Publicar Pesquisa do Cliente
              </Button>
            </div>
          </div>
        ) : (
          <></>
        )}

        {/* STEP 3: Products Entry for Packaging (RF003, RF004, RF005) */}
        {selectedCliente && selectedPackaging ? (
          <div className="space-y-4">
            {/* Header Packaging Details */}
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-8 h-8 p-1 text-gray-600 rounded-full hover:bg-gray-100"
                  onClick={() => setSelectedPackaging(null)}
                >
                  <ChevronLeft size={20} />
                </Button>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Embalagem: {selectedPackaging}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Preencha as informações de todos os produtos listados
                    abaixo.
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setSelectedPackaging(null)}
              >
                Voltar às Embalagens
              </Button>
            </div>

            {/* Products List */}
            {isLoadingProducts ? (
              <div className="py-12 text-xs text-center text-gray-400 bg-white border border-gray-200 rounded-xl">
                Carregando produtos da embalagem...
              </div>
            ) : !packagingProducts || packagingProducts.length === 0 ? (
              <div className="py-12 text-xs text-center text-gray-400 bg-white border border-gray-200 rounded-xl">
                Nenhum produto cadastrado para esta embalagem.
              </div>
            ) : (
              <div className="space-y-3">
                {packagingProducts.map((prod) => {
                  const state = formProducts[prod.SK_PRODUTO] || {
                    VL_PRECO_COMPRA: '',
                    QTD_VOLUME_COMPRA: '',
                    VL_PRECO_VENDA: '',
                    isNotResearched: false,
                  };

                  return (
                    <div
                      key={prod.SK_PRODUTO}
                      className={`bg-white border rounded-2xl p-4 shadow-sm space-y-3 transition-all ${
                        state.isNotResearched
                          ? 'border-amber-300 bg-amber-50/20'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Product Header & Toggle */}
                      <div className="flex items-start justify-between gap-3 pb-2 border-b border-gray-100">
                        <div>
                          <h4 className="text-sm font-bold leading-tight text-gray-900">
                            {prod.DS_PRODUTO}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Marca:{' '}
                            <span className="font-medium text-gray-700">
                              {prod.DD_MARCA || 'N/I'}
                            </span>{' '}
                            • Fabricante:{' '}
                            <span className="font-medium text-gray-700">
                              {prod.DS_DETENTOR || 'N/I'}
                            </span>
                          </p>
                        </div>

                        <label className="flex items-center gap-1.5 cursor-pointer bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 shrink-0">
                          <input
                            type="checkbox"
                            checked={state.isNotResearched}
                            onChange={(e) =>
                              handleToggleNotResearched(
                                prod.SK_PRODUTO,
                                e.target.checked,
                              )
                            }
                            className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-xs font-semibold text-gray-700">
                            Não Pesquisado
                          </span>
                        </label>
                      </div>

                      {/* Fields: Non-research reason vs Prices */}
                      {state.isNotResearched ? (
                        <div className="space-y-1.5 pt-1">
                          <label className="flex items-center gap-1 text-xs font-semibold text-amber-900">
                            <Ban size={14} />
                            Motivo da Não Pesquisa *
                          </label>
                          <Select
                            value={
                              state.SK_MOTIVO_NAO_PESQUISA
                                ? String(state.SK_MOTIVO_NAO_PESQUISA)
                                : ''
                            }
                            onChange={(val) =>
                              handleProductChange(
                                prod.SK_PRODUTO,
                                'SK_MOTIVO_NAO_PESQUISA',
                                Number(val),
                              )
                            }
                            options={(
                              (dataReasons as NonResearchReason[]) || []
                            ).map((r) => ({
                              value: String(r.SK_MOTIVO_NAO_PESQUISA),
                              label: r.DS_MOTIVO_NAO_PESQUISA,
                            }))}
                            placeholder="Selecione o motivo da não pesquisa..."
                            className="h-10 text-xs bg-white"
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 pt-1 sm:grid-cols-3">
                          <div>
                            <label className="block mb-1 text-xs font-semibold text-gray-700">
                              Preço Compra (Caixa)*
                            </label>
                            <Input
                              inputMode="numeric"
                              placeholder="R$ 0,00"
                              className="h-10 text-xs bg-white"
                              value={formatCurrencyBR(state.VL_PRECO_COMPRA)}
                              onChange={(e) =>
                                handleProductChange(
                                  prod.SK_PRODUTO,
                                  'VL_PRECO_COMPRA',
                                  parseCurrencyBR(e.target.value),
                                )
                              }
                            />
                          </div>

                          <div>
                            <label className="block mb-1 text-xs font-semibold text-gray-700">
                              Volume Semanal (Caixa)*
                            </label>
                            <Input
                              type="number"
                              placeholder="0"
                              className="h-10 text-xs bg-white"
                              value={state.QTD_VOLUME_COMPRA}
                              onChange={(e) =>
                                handleProductChange(
                                  prod.SK_PRODUTO,
                                  'QTD_VOLUME_COMPRA',
                                  parseInt(e.target.value) || 0,
                                )
                              }
                            />
                          </div>

                          <div>
                            <label className="block mb-1 text-xs font-semibold text-gray-700">
                              Preço Venda (Unidade)*
                            </label>
                            <Input
                              inputMode="numeric"
                              placeholder="R$ 0,00"
                              className="h-10 text-xs bg-white"
                              value={formatCurrencyBR(state.VL_PRECO_VENDA)}
                              onChange={(e) =>
                                handleProductChange(
                                  prod.SK_PRODUTO,
                                  'VL_PRECO_VENDA',
                                  parseCurrencyBR(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom Actions for Packaging */}
            <div className="flex items-center justify-between gap-3 p-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPackaging(null)}
                className="h-10 px-4 text-xs"
              >
                Cancelar / Voltar
              </Button>

              <Button
                onClick={handleSavePackaging}
                className="gap-2 px-6 text-xs font-bold text-white bg-green-600 shadow-md h-11 hover:bg-green-700"
                disabled={saveMutation.isPending || publishMutation.isPending}
              >
                {saveMutation.isPending || publishMutation.isPending ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Check size={18} />
                )}
                Concluir Embalagem {selectedPackaging}
              </Button>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
